/**
 * The billing rules pre-bill checks on this case: the firm's own and those
 * from the client's or company's guidelines, by source. Admins turn a rule
 * off for this case only (say, a client waived its research cap here); a
 * rule turned off at its source is off everywhere.
 */
import { BookCheck } from "lucide-react";
import type { FC } from "react";

import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import { ruleActions } from "../actions";
import { useCaseRules } from "../queries";
import { ruleTitle, severityText } from "../rules/ruleModel";
import type { AppliedRule } from "../types";

const bySource = (rules: AppliedRule[]) => {
  const groups = new Map<string, { label: string; rules: AppliedRule[] }>();
  for (const rule of rules) {
    const group = groups.get(rule.source_id) ?? {
      label: rule.source_label,
      rules: []
    };
    group.rules.push(rule);
    groups.set(rule.source_id, group);
  }
  return [...groups.entries()];
};

export const CaseRules: FC<{ individualId: string }> = ({ individualId }) => {
  const { data } = useCaseRules(individualId);
  if (!data?.rules.length) return null;
  const on = data.rules.filter((r) => r.on).length;
  return (
    <div className="flex flex-col gap-2" data-testid="case-rules">
      <span className="flex items-center gap-2">
        <BookCheck className="size-3.5 text-fg-3" />
        <span className="text-base font-medium">Pre-bill rules</span>
        <span className="text-sm text-fg-3">
          {on} of {data.rules.length} on for this case
        </span>
      </span>
      <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
        {bySource(data.rules).map(([sourceId, group]) => (
          <div
            key={sourceId}
            className="border-b border-line-2 last:border-b-0"
          >
            <div className="bg-fill-1 px-3 py-1.5 text-sm text-fg-3">
              {group.label}
            </div>
            {group.rules.map((rule) => (
              <div
                key={rule.id}
                className="flex min-h-9 items-center gap-2.5 px-3 py-1.5"
                data-testid="case-rule"
                title={rule.quote}
              >
                <Switch
                  aria-label={`Check "${rule.label}" on this case`}
                  checked={rule.on}
                  disabled={!data.can_edit || !rule.enabled}
                  onCheckedChange={(next) =>
                    void ruleActions.setCaseRule(individualId, rule, next)
                  }
                />
                <span
                  className={cn(
                    "flex min-w-0 flex-1 flex-col",
                    !rule.on && "opacity-60"
                  )}
                >
                  <span className="truncate text-base">{ruleTitle(rule)}</span>
                  <span className="truncate text-sm text-fg-3">
                    {rule.enabled ? rule.summary : `Off in ${group.label}`}
                  </span>
                </span>
                <Badge variant="secondary" className="h-5 text-sm font-normal">
                  {severityText(rule.severity)}
                </Badge>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
